export const LOCATION_DISPLAY_MAX = 500;

export type EventLocationParts = {
  formatted?: string | null;
  note?: string | null;
  freeform?: string | null;
};

export type EventLocationGeo = {
  location?: string | null;
  location_place_id?: string | null;
  location_formatted?: string | null;
  location_lat?: number | string | null;
  location_lng?: number | string | null;
  location_note?: string | null;
};

function trimToNull(value?: string | null): string | null {
  const text = value?.trim();
  return text ? text : null;
}

function clampDisplay(value: string): string {
  if (value.length <= LOCATION_DISPLAY_MAX) return value;
  return value.slice(0, LOCATION_DISPLAY_MAX);
}

export function formatEventLocation({ formatted, note, freeform }: EventLocationParts): string {
  const picked = trimToNull(formatted);
  const room = trimToNull(note);
  const typed = trimToNull(freeform);

  if (picked && room) return clampDisplay(`${room} · ${picked}`);
  if (picked) return clampDisplay(picked);
  if (typed && room && typed !== room) return clampDisplay(`${room} · ${typed}`);
  if (typed) return clampDisplay(typed);
  if (room) return clampDisplay(room);
  return '';
}

export function canOpenInMaps(event: Pick<EventLocationGeo, 'location_place_id'>): boolean {
  return Boolean(trimToNull(event.location_place_id));
}

export function parseCoord(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function mapsQuery(event: EventLocationGeo): string {
  return trimToNull(event.location_formatted) || '';
}

function mapsPlaceId(event: EventLocationGeo): string | null {
  const raw = trimToNull(event.location_place_id);
  if (!raw) return null;
  return raw.replace(/^places\//, '');
}

export function appleMapsUrl(event: EventLocationGeo): string {
  const query = mapsQuery(event);
  const lat = parseCoord(event.location_lat);
  const lng = parseCoord(event.location_lng);
  const parts: string[] = [];
  if (query) parts.push(`q=${encodeURIComponent(query)}`);
  if (lat !== null && lng !== null) parts.push(`ll=${encodeURIComponent(`${lat},${lng}`)}`);
  return `https://maps.apple.com/?${parts.join('&')}`;
}

export function googleMapsUrl(event: EventLocationGeo): string {
  const query = mapsQuery(event);
  const placeId = mapsPlaceId(event);
  const parts = ['api=1'];
  if (query) parts.push(`query=${encodeURIComponent(query)}`);
  else if (placeId) parts.push(`query=${encodeURIComponent(placeId)}`);
  if (placeId) parts.push(`query_place_id=${encodeURIComponent(placeId)}`);
  return `https://www.google.com/maps/search/?${parts.join('&')}`;
}

export type PickedEventPlace = {
  placeId: string;
  formatted: string;
  latitude?: number;
  longitude?: number;
};

export function hydrateEventLocation(event: EventLocationGeo): {
  address: string;
  note: string;
  place: PickedEventPlace | null;
} {
  const note = trimToNull(event.location_note) || '';
  const placeId = trimToNull(event.location_place_id);
  if (placeId) {
    return {
      address: trimToNull(event.location_formatted) || trimToNull(event.location) || '',
      note,
      place: {
        placeId,
        formatted: trimToNull(event.location_formatted) || '',
        latitude: parseCoord(event.location_lat) ?? undefined,
        longitude: parseCoord(event.location_lng) ?? undefined,
      },
    };
  }

  const display = trimToNull(event.location) || '';
  const prefix = note ? `${note} · ` : '';
  const address = prefix && display.startsWith(prefix) ? display.slice(prefix.length) : display;
  return { address, note, place: null };
}

export function toCreateLocationPayload(
  address: string,
  note: string,
  place: PickedEventPlace | null
) {
  const location = formatEventLocation({
    formatted: place?.formatted,
    note,
    freeform: address,
  });
  return {
    location: location || undefined,
    locationPlaceId: place?.placeId,
    locationLat: place?.latitude,
    locationLng: place?.longitude,
    locationFormatted: place?.formatted,
    locationNote: note.trim() || undefined,
  };
}

export function toUpdateLocationPayload(
  address: string,
  note: string,
  place: PickedEventPlace | null
) {
  const created = toCreateLocationPayload(address, note, place);
  return {
    location: created.location ?? null,
    location_place_id: created.locationPlaceId ?? null,
    location_lat: created.locationLat ?? null,
    location_lng: created.locationLng ?? null,
    location_formatted: created.locationFormatted ?? null,
    location_note: created.locationNote ?? null,
  };
}

export function androidGeoUrl(event: EventLocationGeo): string {
  const lat = parseCoord(event.location_lat);
  const lng = parseCoord(event.location_lng);
  const query = encodeURIComponent(mapsQuery(event));
  const pin = lat !== null && lng !== null ? `${lat},${lng}` : '0,0';
  return `geo:${pin}?q=${query}`;
}
